/**
 * Resolving a picked reference id back to its display name.
 *
 * `ClientSearch` / `EntitySearch` (from `@jsq/common-components`) hand back only
 * an id on selection. Vault attribute values persist as text — the backend's
 * `VaultDocumentAttributeValueInput` has `valueText` / `valueNumber` /
 * `valueDate` / `valueBoolean` and no reference-id column — so the NAME is what
 * we store, otherwise documents read as "Client: arena-1041" everywhere the
 * attribute is displayed or filtered.
 *
 * Requires an `ApolloProvider` above it, so callers must only mount this on the
 * connected path (see `UploadDocumentModal`'s `referenceSearch` prop).
 */
import { useCallback, useRef } from "react";
import {
  useVaultArenaNamesLazyQuery,
  useVaultEntityNamesLazyQuery,
  useVaultInvestorPositionNamesLazyQuery,
} from "#graphql/supergraphHooks";

export type ReferenceNameResolvers = {
  /** Display name for an arena/client id, or undefined when it can't be resolved. */
  resolveClientName: (id: string) => Promise<string | undefined>;
  /**
   * Display name for a data-vault entity id, or undefined when it can't be
   * resolved. `clientId` (the owning arena) is required — main-subgraph rejects
   * an entity-only filter — so an entity picked without a client can't be named.
   */
  resolveEntityName: (id: string, clientId?: string) => Promise<string | undefined>;
  /**
   * Display name for an investor position id, or undefined when it can't be
   * resolved. Both `arenaIds` and `entityIds` are required — main-subgraph rejects
   * an entity-only filter — so a position picked without both scope arrays returns
   * undefined.
   *
   * Label is currently just `account.name`.
   * TODO: restore `${account.name} — ${investorGroup.name}` format once the
   * subgraph exposes `InvestorPosition.investorGroup`.
   */
  resolveInvestorPositionName: (
    id: string,
    arenaIds?: string[],
    entityIds?: string[],
  ) => Promise<string | undefined>;
};

export function useReferenceNames(): ReferenceNameResolvers {
  const [fetchArenaNames] = useVaultArenaNamesLazyQuery({ fetchPolicy: "no-cache" });
  const [fetchEntityNames] = useVaultEntityNamesLazyQuery({ fetchPolicy: "no-cache" });
  const [fetchInvestorPositionNames] = useVaultInvestorPositionNamesLazyQuery({
    fetchPolicy: "no-cache",
  });
  // Ids never change name mid-session; one lookup each is enough.
  const cache = useRef<Record<string, string>>({});

  const resolveClientName = useCallback(
    async (id: string) => {
      if (cache.current[id]) return cache.current[id];
      const { data } = await fetchArenaNames({ variables: { arenaIds: [id] } });
      const name = data?.arenas?.edges.find((e) => e?.node.id === id)?.node.name;
      if (name) cache.current[id] = name;
      return name ?? undefined;
    },
    [fetchArenaNames],
  );

  const resolveEntityName = useCallback(
    async (id: string, clientId?: string) => {
      if (cache.current[id]) return cache.current[id];
      // Without the arena the query is rejected outright, so don't send it.
      if (!clientId) return undefined;
      const { data } = await fetchEntityNames({
        variables: { arenaIds: [clientId], entityIds: [id] },
      });
      const name = data?.dataVaultEntities?.edges.find((e) => e?.node.id === id)?.node.name;
      if (name) cache.current[id] = name;
      return name ?? undefined;
    },
    [fetchEntityNames],
  );

  const resolveInvestorPositionName = useCallback(
    async (id: string, arenaIds?: string[], entityIds?: string[]) => {
      if (cache.current[id]) return cache.current[id];
      // Both scope arrays are required by the main subgraph — bail out early
      // when either is absent or empty to avoid a rejected request.
      if (!arenaIds?.length || !entityIds?.length) return undefined;
      const { data } = await fetchInvestorPositionNames({
        variables: { arenaIds, entityIds },
      });
      const entities = data?.dataVaultEntities?.edges ?? [];
      for (const entityEdge of entities) {
        const posEdge = entityEdge?.node.owningInvestorPositions?.edges.find(
          (e) => e?.node.id === id,
        );
        if (posEdge) {
          const { account } = posEdge.node;
          // account can be null in the schema; skip positions with no account
          if (!account) continue;
          const name = account.name;
          cache.current[id] = name;
          return name;
        }
      }
      return undefined;
    },
    [fetchInvestorPositionNames],
  );

  return { resolveClientName, resolveEntityName, resolveInvestorPositionName };
}
